// ------------ import external dependencies --------------
import React, { Suspense, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { H } from "highlight.run";
import "react-toastify/dist/ReactToastify.css";
// ----------- import internal dependencies ------------
import "./css/main.css";

import AllRoutes from "./routes/routes";
import CustomLoader from "./components/CustomLoader";
import { ErrorBoundary } from "./components/Errors/ErrorBoundary";
import ErrorFallBackCard from "./components/Errors/ErrorFallBackCard";
import { ConfigProvider } from "antd";
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count: number, error: any) =>
        error?.response?.status !== 500 || false,
      refetchOnWindowFocus: false,
    },
  },
});

const { VITE_HIGHLIGHT_ENV, VITE_HIGHLIGHT_ID, VITE_AUTH_API_URL } =
  import.meta.env;
const recordSessions = ["production", "uat"];

// Global state for error message - will be accessed by both the error handler and React component
let globalShowErrorMessage = false;
let errorMessageCallback: (() => void) | null = null;

// Function to set the error message and trigger React re-render
function setShowErrorMessage(show: boolean) {
  globalShowErrorMessage = show;
  if (errorMessageCallback) {
    errorMessageCallback();
  }
}

recordSessions.includes(VITE_HIGHLIGHT_ENV || "production") &&
  H.init(VITE_HIGHLIGHT_ID, {
    environment: VITE_HIGHLIGHT_ENV,
    serviceName: "frontend-app",
    tracingOrigins: true,
    networkRecording: {
      enabled: true,
      recordHeadersAndBody: true,
      urlBlocklist: [
        // insert full or partial urls that you don't want to record here
        // Out of the box, Highlight will not record these URLs (they can be safely removed):
        "https://www.googleapis.com/identitytoolkit",
        "https://securetoken.googleapis.com",
        // Login and password bodies, and the bearer token header, go to this host.
        ...(VITE_AUTH_API_URL ? [VITE_AUTH_API_URL] : []),
      ],
    },
  });

// Smart chunk loading error handler - only reloads for actual chunk failures
const MAX_RELOAD_ATTEMPTS = 2;
const RELOAD_COOLDOWN = 5000; // 5 seconds
const CHUNK_ERROR_KEY = "mitigate-risk-chunk-errors";

// Get persisted values from sessionStorage (survives page reloads)
function getChunkErrorState() {
  try {
    const stored = sessionStorage.getItem(CHUNK_ERROR_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        attempts: parsed.attempts || 0,
        lastReloadTime: parsed.lastReloadTime || 0,
      };
    }
  } catch (e) {
    console.warn("Failed to parse chunk error state from sessionStorage");
  }
  return { attempts: 0, lastReloadTime: 0 };
}

// Save state to sessionStorage
function saveChunkErrorState(attempts: number, lastReloadTime: number) {
  try {
    sessionStorage.setItem(
      CHUNK_ERROR_KEY,
      JSON.stringify({
        attempts,
        lastReloadTime,
      })
    );
  } catch (e) {
    console.warn("Failed to save chunk error state to sessionStorage");
  }
}

// Clear the error state (call this on successful app load after some time)
function clearChunkErrorState() {
  try {
    sessionStorage.removeItem(CHUNK_ERROR_KEY);
  } catch (e) {
    // Ignore errors
  }
}

function isChunkLoadingError(error: any): boolean {
  if (!error) return false;

  const errorMessage = error.message || error.reason?.message || "";
  const errorStack = error.stack || error.reason?.stack || "";
  const filename = error.filename || "";

  // Specific patterns for Vite chunk loading errors
  const chunkErrorPatterns = [
    /Loading chunk \d+ failed/i,
    /Loading CSS chunk \d+ failed/i,
    /ChunkLoadError/i,
    /Failed to import/i,
    /Cannot resolve module/i,
    /Failed to fetch dynamically imported module/i,
  ];

  // Must be a network/loading error, not a syntax error
  const isNetworkError = chunkErrorPatterns.some(
    (pattern) => pattern.test(errorMessage) || pattern.test(errorStack)
  );

  // Additional check: filename should contain chunk-related patterns
  const isChunkFile =
    /chunk|assets.*\.js/.test(filename) ||
    errorMessage.includes("/_app/") ||
    errorMessage.includes("/assets/");

  return isNetworkError && (isChunkFile || errorMessage.includes("Loading"));
}

function handleChunkError(error: any, source: string) {
  if (!isChunkLoadingError(error)) {
    return; // Not a chunk error, don't reload
  }

  const now = Date.now();
  const state = getChunkErrorState();

  // Prevent too frequent reloads
  if (now - state.lastReloadTime < RELOAD_COOLDOWN) {
    console.warn("Chunk reload attempted too soon, skipping");
    setShowErrorMessage(true);
    return;
  }

  // Prevent infinite reload loops
  if (state.attempts >= MAX_RELOAD_ATTEMPTS) {
    console.error(
      "Max chunk reload attempts reached. Please refresh manually."
    );
    // Could show a user-friendly message here instead
    setShowErrorMessage(true);
    return;
  }

  const newAttempts = state.attempts + 1;
  saveChunkErrorState(newAttempts, now);

  console.warn(
    `Chunk loading failed (${source}), attempt ${newAttempts}/${MAX_RELOAD_ATTEMPTS}`,
    error
  );

  // Save any unsaved data warning
  if (document.querySelector('form input[value]:not([value=""])')) {
    if (
      !confirm(
        "A resource failed to load. The page needs to refresh. Any unsaved changes will be lost. Continue?"
      )
    ) {
      return;
    }
  }

  // Force reload bypassing cache
  window.location.reload();
}

// Handle Promise-based chunk loading errors (dynamic imports)
window.addEventListener("unhandledrejection", (event) => {
  handleChunkError(event.reason, "unhandledrejection");
});

// Handle script loading errors
window.addEventListener("error", (event) => {
  // Only handle resource loading errors, not JavaScript runtime errors
  if (event.target !== window && (event.target as any)?.tagName) {
    const target = event.target as HTMLElement;
    if (target.tagName === "SCRIPT" || target.tagName === "LINK") {
      handleChunkError(
        {
          message: `Failed to load ${target.tagName}: ${(target as any).src || (target as any).href}`,
          filename: (target as any).src || (target as any).href,
        },
        "resource-error"
      );
    }
  } else {
    // For non-resource errors, only handle if they match chunk patterns
    handleChunkError(event, "script-error");
  }
});

// Expose functions globally for testing and debugging
if (import.meta.env.DEV) {
  (window as any).chunkErrorDebug = {
    getState: getChunkErrorState,
    clearState: clearChunkErrorState,
    testChunkError: () => {
      window.dispatchEvent(
        new PromiseRejectionEvent("unhandledrejection", {
          promise: Promise.reject(new Error("Loading chunk 999 failed")),
          reason: new Error("Loading chunk 999 failed"),
        })
      );
    },
  };
}

function App() {
  const [showErrorMessage, setLocalShowErrorMessage] = useState(
    globalShowErrorMessage
  );

  // Initialize Google Analytics
  React.useEffect(() => {
    const trackingId = import.meta.env.VITE_GA_TRACKING_ID;
    if (!trackingId) {
      console.warn('GA tracking ID not configured');
      return;
    }

    // Check if GA scripts already exist to prevent duplicates on re-mount
    const existingGtagScript = document.querySelector(`script[src*="googletagmanager.com/gtag/js"]`);
    if (existingGtagScript) {
      return;
    }

    const gtagScript = document.createElement("script");
    gtagScript.async = true;
    gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
    document.head.appendChild(gtagScript);

    const gtagConfigScript = document.createElement("script");
    gtagConfigScript.id = 'ga-config-script';
    gtagConfigScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${trackingId}');
    `;
    document.head.appendChild(gtagConfigScript);
  }, []);

  // Set up the callback to allow global error handler to trigger re-render
  React.useEffect(() => {
    errorMessageCallback = () => {
      setLocalShowErrorMessage(globalShowErrorMessage);
    };

    // Initialize state from global variable
    setLocalShowErrorMessage(globalShowErrorMessage);

    return () => {
      errorMessageCallback = null;
    };
  }, []);

  // Clear chunk error state after successful app initialization
  React.useEffect(() => {
    const clearStateTimer = setTimeout(() => {
      // If the app loads successfully and stays loaded for 10 seconds,
      // clear any previous chunk error attempts
      clearChunkErrorState();
    }, 10000);

    return () => clearTimeout(clearStateTimer);
  }, []);

  return (
    <>
      {showErrorMessage && (
        <div className="chunk-error-message">
          Not all resources loaded successfully. Please clear your browser cache
          and reload the page by pressing&nbsp;
          <span className="bold-text">CTRL + F5</span>
        </div>
      )}
      <QueryClientProvider client={queryClient}>
        <ToastContainer style={{ fontSize: "14px" }} />
        <Suspense fallback={<CustomLoader fullscreen />}>
          <ErrorBoundary fallback={<ErrorFallBackCard />}>
            <ConfigProvider
              theme={{
                token: {
                  // Seed Token
                  colorPrimary: "#b580ff",
                  borderRadius: 2,

                  // Alias Token
                  colorBgContainer: "#fff",
                },
              }}
            >
              <RouterProvider router={AllRoutes} />
            </ConfigProvider>
          </ErrorBoundary>
        </Suspense>
      </QueryClientProvider>
    </>
  );
}

export default App;
